import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FaArrowLeft, FaShoppingBag, FaCheckCircle, FaDownload } from "react-icons/fa";
import { API_BASE_URL } from "../../config/apiConfig";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const TYPE_LABELS = {
    lesson_notes: "Lesson Notes",
    revision_guide: "Revision Guide",
    practice_test: "Practice Test",
    video: "Video",
    exam_prep: "Exam Prep Pack",
};

const Marketplace = () => {
    const navigate = useNavigate();
    const [tab, setTab] = useState("browse");
    const [bundles, setBundles] = useState([]);
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [buyingId, setBuyingId] = useState(null);

    const fetchBundles = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/marketplace/bundles`, { headers: authHeaders() });
            const data = await res.json();
            setBundles(data.data || []);
        } catch (err) {
            toast.error("Could not load marketplace");
        }
    };

    const fetchPurchases = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/marketplace/my-purchases`, { headers: authHeaders() });
            const data = await res.json();
            setPurchases(data.data || []);
        } catch (err) {
            // silent
        }
    };

    useEffect(() => {
        Promise.all([fetchBundles(), fetchPurchases()]).finally(() => setLoading(false));
    }, []);

    const purchasedBundleIds = new Set(purchases.map((p) => p.bundle_id));

    const handleBuy = async (bundle) => {
        setBuyingId(bundle.id);
        try {
            const res = await fetch(`${API_BASE_URL}/api/marketplace/purchase/${bundle.id}`, {
                method: "POST",
                headers: authHeaders(),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Failed to start payment");
            window.location.href = data.payment_url;
        } catch (err) {
            toast.error(err.message);
            setBuyingId(null);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <nav className="bg-[#2148C0] shadow-md px-6 py-4 flex items-center gap-4">
                <button onClick={() => navigate("/dashboard")} className="text-white"><FaArrowLeft size={18} /></button>
                <h1 className="text-xl font-bold text-white flex items-center gap-2"><FaShoppingBag /> Marketplace</h1>
            </nav>

            <div className="max-w-4xl mx-auto p-6">
                <div className="flex gap-2 mb-6 bg-white p-1 rounded-lg shadow-sm w-fit">
                    {["browse", "mine"].map((t) => (
                        <button key={t} onClick={() => setTab(t)}
                            className={`px-4 py-2 rounded-md text-sm font-semibold ${tab === t ? "bg-blue600 text-white" : "text-gray-600"}`}>
                            {t === "browse" ? "Browse" : "My Purchases"}
                        </button>
                    ))}
                </div>

                {loading && <p className="text-gray-500">Loading...</p>}

                {tab === "browse" && !loading && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {bundles.length === 0 && <p className="text-gray-500">No content available yet.</p>}
                        {bundles.map((b) => {
                            const owned = purchasedBundleIds.has(b.id);
                            return (
                                <div key={b.id} className="bg-white rounded-xl shadow-sm p-5">
                                    <span className="text-xs uppercase tracking-wide text-blue600 font-semibold">{TYPE_LABELS[b.type] || b.type}</span>
                                    <h3 className="font-bold text-gray-800 mt-1">{b.title}</h3>
                                    <p className="text-sm text-gray-500 mt-1">{b.description}</p>
                                    <p className="text-xs text-gray-400 mt-2">By {b.teacherName}</p>
                                    <div className="flex items-center justify-between mt-4">
                                        <span className="font-bold text-gray-800">₦{b.price.toLocaleString()}</span>
                                        {owned ? (
                                            <button onClick={() => setTab("mine")} className="text-green-600 font-semibold text-sm flex items-center gap-1">
                                                <FaCheckCircle /> Owned
                                            </button>
                                        ) : (
                                            <button disabled={buyingId === b.id} onClick={() => handleBuy(b)}
                                                className="bg-blue600 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
                                                {buyingId === b.id ? "Redirecting..." : "Buy"}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {tab === "mine" && !loading && (
                    <div className="space-y-3">
                        {purchases.length === 0 && <p className="text-gray-500">No purchases yet.</p>}
                        {purchases.map((p) => (
                            <div key={p.id} className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between">
                                <div>
                                    <h3 className="font-semibold text-gray-800">{p.MarketplaceBundle?.title}</h3>
                                    <p className="text-xs text-gray-400">₦{p.amount.toLocaleString()} · {new Date(p.createdAt).toLocaleDateString()}</p>
                                </div>
                                {p.MarketplaceBundle?.content_url && (
                                    <a href={p.MarketplaceBundle.content_url} target="_blank" rel="noreferrer"
                                        className="text-blue600 font-semibold text-sm flex items-center gap-1">
                                        <FaDownload /> Open
                                    </a>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Marketplace;
